import { statusBlue, statusGreen, statusOrange } from 'helpers/colors';
import { IMPROVEMENTS_BADGE_DISMISS } from 'helpers/constants';
import { improvementsBadgeEmitter } from 'components/Pages/Admin/event/eventEmitter';
import { postFirstAccessCheck } from 'services/user';

export enum EImprovementCategory {
  feature = 'feature',
  improvement = 'improvement',
  fix = 'fix'
}

export type ImprovementCategoryConfig = {
  label: string;
  color: string;
  background: string;
  border: string;
};

export const IMPROVEMENT_CATEGORY_CONFIG: Record<EImprovementCategory, ImprovementCategoryConfig> = {
  [EImprovementCategory.feature]: {
    label: 'Nova Funcionalidade',
    color: statusGreen.contrast,
    background: statusGreen.light,
    border: statusGreen.border
  },
  [EImprovementCategory.improvement]: {
    label: 'Melhoria',
    color: statusBlue.contrast,
    background: statusBlue.light,
    border: statusBlue.border
  },
  [EImprovementCategory.fix]: {
    label: 'Correção',
    color: statusOrange.contrast,
    background: statusOrange.light,
    border: statusOrange.border
  }
};

export type Improvement = {
  id: number;
  title: string;
  releaseDate: string;
  description: string;
  category: EImprovementCategory;
};

export const IMPROVEMENTS: Improvement[] = [
  {
    id: 1,
    title: 'Nova identidade visual e navegação',
    releaseDate: '2026-04-28',
    description:
      'Padronizamos as cores em todo o sistema, modernizamos a barra lateral com indicação da página ativa e adicionamos ícones aos filtros rápidos da listagem de multas.',
    category: EImprovementCategory.improvement
  },
  {
    id: 3,
    title: 'Nova tela de Relatórios',
    releaseDate: '2026-05-15',
    description:
      'Criamos a tela de Relatórios, onde você pode exportar em Excel os relatórios Geral de multas, Boletos Disponíveis, Piso Mínimo de Frete e Excesso de Peso, com o arquivo gerado já nomeado com a data do dia.',
    category: EImprovementCategory.feature
  },
  {
    id: 5,
    title: 'Piso Mínimo de Frete no detalhe da multa',
    releaseDate: '2026-06-19',
    description:
      'O detalhe da multa ganhou a aba Piso Mínimo de Frete, que reúne os dados do frete (origem, destino, distância e coeficientes da ANTT) e apresenta o cálculo do piso mínimo de frete de forma clara e transparente.',
    category: EImprovementCategory.feature
  },
  {
    id: 7,
    title: 'Integração com o SAP no detalhe da multa',
    releaseDate: '2026-06-19',
    description:
      'O detalhe da multa passou a exibir um card da integração com o SAP, indicando quando ainda não há dados vinculados e mostrando a data da última sincronização.',
    category: EImprovementCategory.feature
  },
  {
    id: 9,
    title: 'Path de Atualizações',
    releaseDate: '2026-06-19',
    description:
      'Criamos esta linha do tempo para você acompanhar, de forma visual e cronológica, todas as novidades, melhorias e correções entregues no sistema. Um indicador no menu e no topo da tela avisa sempre que houver atualizações que você ainda não viu.',
    category: EImprovementCategory.feature
  },
  {
    id: 11,
    title: 'Correções de erros e melhorias de estabilidade',
    releaseDate: '2026-07-10',
    description:
      'Realizamos correções de erros e ajustes internos para deixar o sistema mais estável, rápido e confiável no seu dia a dia.',
    category: EImprovementCategory.fix
  }
];

export const IMPROVEMENTS_LAST_SEEN_KEY = 'improvements_last_seen_id';

export function getLatestImprovementId(): number {
  return IMPROVEMENTS.reduce((max, item) => (item.id > max ? item.id : max), 0);
}

export function getLastSeenImprovementId(): number {
  const stored = Number(localStorage.getItem(IMPROVEMENTS_LAST_SEEN_KEY));
  return Number.isFinite(stored) ? stored : 0;
}

export function getUnseenImprovementsCount(): number {
  const lastSeen = getLastSeenImprovementId();
  return IMPROVEMENTS.filter(item => item.id > lastSeen).length;
}

export function markImprovementsAsSeen(): void {
  localStorage.setItem(IMPROVEMENTS_LAST_SEEN_KEY, String(getLatestImprovementId()));
}

export function isImprovementsFirstAccess(): boolean {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Boolean(user?.tutorial?.financial);
  } catch {
    return false;
  }
}

export function dismissImprovementsFirstAccess(): void {
  if (!isImprovementsFirstAccess()) return;

  postFirstAccessCheck({ tutorial: { financial: false } })
    .then(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user?.tutorial) {
          user.tutorial.financial = false;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (error) {
        console.error('Falha ao registrar primeiro acesso', error);
      }

      improvementsBadgeEmitter.emit(IMPROVEMENTS_BADGE_DISMISS);
    })
    .catch(() => undefined);
}
